import React from "react";
import { View } from "react-native-web";

type Insets = { top: number; bottom: number; left: number; right: number };
type Frame = { x: number; y: number; width: number; height: number };
type Metrics = { insets: Insets; frame: Frame };

const DEFAULT_INSETS: Insets = { top: 0, bottom: 0, left: 0, right: 0 };
const DEFAULT_FRAME: Frame = { x: 0, y: 0, width: 0, height: 0 };

const SafeAreaInsetsContext = React.createContext<Insets>(DEFAULT_INSETS);
const SafeAreaFrameContext = React.createContext<Frame>(DEFAULT_FRAME);

export { SafeAreaInsetsContext };

interface SafeAreaProviderProps {
  children?: React.ReactNode;
  initialMetrics?: Metrics;
}

export function SafeAreaProvider({ children, initialMetrics }: SafeAreaProviderProps): React.ReactElement {
  const insets = initialMetrics?.insets ?? DEFAULT_INSETS;
  const frame = initialMetrics?.frame ?? DEFAULT_FRAME;
  return React.createElement(
    SafeAreaInsetsContext.Provider,
    { value: insets },
    React.createElement(SafeAreaFrameContext.Provider, { value: frame }, children)
  );
}

export const useSafeAreaInsets = (): Insets => React.useContext(SafeAreaInsetsContext);

export const useSafeAreaFrame = (): Frame => React.useContext(SafeAreaFrameContext);

type Edge = "top" | "right" | "bottom" | "left";

interface SafeAreaViewProps {
  children?: React.ReactNode;
  style?: unknown;
  edges?: readonly Edge[];
  mode?: "padding" | "margin";
  [key: string]: unknown;
}

export function SafeAreaView({
  children,
  style,
  edges,
  mode = "padding",
  ...rest
}: SafeAreaViewProps): React.ReactElement {
  const insets = useSafeAreaInsets();
  const appliedEdges: Edge[] = edges ? [...edges] : ["top", "right", "bottom", "left"];
  const prop = mode === "margin" ? "margin" : "padding";
  const insetStyle: Record<string, number> = {};
  if (appliedEdges.includes("top")) insetStyle[`${prop}Top`] = insets.top;
  if (appliedEdges.includes("bottom")) insetStyle[`${prop}Bottom`] = insets.bottom;
  if (appliedEdges.includes("left")) insetStyle[`${prop}Left`] = insets.left;
  if (appliedEdges.includes("right")) insetStyle[`${prop}Right`] = insets.right;
  return React.createElement(View, { style: [insetStyle, style], ...rest }, children);
}

export const initialWindowMetrics: Metrics = {
  insets: DEFAULT_INSETS,
  frame: DEFAULT_FRAME,
};

export default {
  SafeAreaView,
  SafeAreaProvider,
  useSafeAreaInsets,
  useSafeAreaFrame,
  SafeAreaInsetsContext,
  initialWindowMetrics,
};
